import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Search, Edit2, Trash2, Layers, X, CheckCircle2, Upload } from 'lucide-react';

// Admin materials catalogue — add/edit/remove materials WITH civil sizes/specs.
// Talks to /materials (GET open; POST/PUT/DELETE require admin — enforced server-side).
export default function MaterialsManager({ api, showToast }) {
    const EMPTY = { category: '', name: '', spec: '', size: '', unit: '', hsn: '', gst: '', item_code: '', cost_code: '', brand: '', variant: '', weight: '', colour: '', length: '', breadth: '', height: '', diameter: '', in_stock: '' };

    const [items, setItems]       = useState([]);
    const [loading, setLoading]   = useState(true);
    const [search, setSearch]     = useState('');
    const [catFilter, setCatFilter] = useState('All');
    const [showForm, setShowForm] = useState(false);
    const [editId, setEditId]     = useState(null);
    const [form, setForm]         = useState(EMPTY);
    const [saving, setSaving]     = useState(false);

    const load = async () => {
        setLoading(true);
        try { setItems(await api('materials') || []); }
        catch (e) { showToast && showToast('Could not load materials'); }
        setLoading(false);
    };
    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const categories = useMemo(
        () => [...new Set(items.map(m => m.category).filter(Boolean))].sort(),
        [items]
    );

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return items.filter(m =>
            (catFilter === 'All' || m.category === catFilter) &&
            (!q || [m.category, m.name, m.spec, m.size, m.unit]
                .some(v => (v || '').toLowerCase().includes(q)))
        );
    }, [items, search, catFilter]);

    const openAdd  = () => { setForm(EMPTY); setEditId(null); setShowForm(true); };
    const openEdit = (m) => {
        setForm({ category: m.category || '', name: m.name || '', spec: m.spec || '',
                  size: m.size || '', unit: m.unit || '', hsn: m.hsn || '',
                  gst: m.gst || '', item_code: m.item_code || '', cost_code: m.cost_code || '',
                  brand: m.brand || '', variant: m.variant || '', weight: m.weight || '',
                  colour: m.colour || '', length: m.length || '', breadth: m.breadth || '',
                  height: m.height || '', diameter: m.diameter || '',
                  in_stock: (m.in_stock ?? '') === null ? '' : String(m.in_stock ?? '') });
        setEditId(m.id); setShowForm(true);
    };
    const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const save = async () => {
        if (!form.category.trim() || !form.name.trim()) {
            showToast && showToast('Category and name are required'); return;
        }
        setSaving(true);
        try {
            if (editId) await api(`materials/${editId}`, 'PUT', form);
            else        await api('materials', 'POST', form);
            setShowForm(false); setForm(EMPTY); setEditId(null);
            await load();
            showToast && showToast(editId ? 'Material updated' : 'Material added');
        } catch (e) {
            showToast && showToast('Save failed — you may need admin rights');
        }
        setSaving(false);
    };

    const remove = async (m) => {
        if (!window.confirm(`Remove "${m.name}${m.size ? ' · ' + m.size : ''}"? A backup is kept on the NAS.`)) return;
        try { await api(`materials/${m.id}`, 'DELETE'); await load(); showToast && showToast('Material removed'); }
        catch (e) { showToast && showToast('Delete failed'); }
    };

    // ── Bulk import from Excel / CSV ──────────────────────────────
    const [importRows, setImportRows] = useState(null);   // parsed preview, or null
    const [importing, setImporting]   = useState(false);
    const [importDone, setImportDone] = useState(0);

    const mapHeader = (h) => {
        const k = String(h || '').trim().toLowerCase().replace(/[\s_/.-]+/g, '');
        if (['category', 'cat', 'group', 'type'].includes(k)) return 'category';
        if (['material', 'name', 'materialname', 'item', 'itemname', 'description'].includes(k)) return 'name';
        if (['grade', 'spec', 'gradespec', 'specgrade', 'specification', 'gradeorspec'].includes(k)) return 'spec';
        if (['size', 'dimension', 'dimensions', 'sizemm'].includes(k)) return 'size';
        if (['unit', 'uom', 'units'].includes(k)) return 'unit';
        if (['hsn', 'hsncode', 'hsnsac', 'sac'].includes(k)) return 'hsn';
        if (['gst', 'gstrate', 'gst%', 'tax'].includes(k)) return 'gst';
        if (['itemcode', 'code', 'sku'].includes(k)) return 'item_code';
        if (['costcode'].includes(k)) return 'cost_code';
        if (['brand', 'make', 'manufacturer'].includes(k)) return 'brand';
        if (['variant', 'model'].includes(k)) return 'variant';
        if (['weight', 'wt'].includes(k)) return 'weight';
        if (['colour', 'color'].includes(k)) return 'colour';
        if (['length', 'len'].includes(k)) return 'length';
        if (['breadth', 'width'].includes(k)) return 'breadth';
        if (['height', 'ht'].includes(k)) return 'height';
        if (['diameter', 'dia'].includes(k)) return 'diameter';
        if (['instock', 'stock', 'qty', 'quantity', 'openingstock'].includes(k)) return 'in_stock';
        return null;
    };

    const rowsToMaterials = (rows) => {
        if (!rows || rows.length < 2) return [];
        const headers = rows[0].map(mapHeader);
        if (!headers.includes('category') && !headers.includes('name')) return [];
        const out = [];
        for (let i = 1; i < rows.length; i++) {
            const r = rows[i];
            if (!r || r.every(c => c === '' || c == null)) continue;
            const obj = { category: '', name: '', spec: '', size: '', unit: '', hsn: '', gst: '', item_code: '', cost_code: '', brand: '', variant: '', weight: '', colour: '', length: '', breadth: '', height: '', diameter: '', in_stock: '' };
            headers.forEach((key, idx) => { if (key) obj[key] = String(r[idx] ?? '').trim(); });
            if (obj.category || obj.name) out.push(obj);
        }
        return out;
    };

    const parseCSV = (text) => {
        const rows = []; let row = [], cur = '', inQ = false;
        for (let i = 0; i < text.length; i++) {
            const c = text[i];
            if (inQ) {
                if (c === '"' && text[i + 1] === '"') { cur += '"'; i++; }
                else if (c === '"') inQ = false;
                else cur += c;
            } else if (c === '"') inQ = true;
            else if (c === ',') { row.push(cur); cur = ''; }
            else if (c === '\r') { /* skip */ }
            else if (c === '\n') { row.push(cur); rows.push(row); row = []; cur = ''; }
            else cur += c;
        }
        if (cur !== '' || row.length) { row.push(cur); rows.push(row); }
        return rows;
    };

    const loadSheetJS = () => new Promise((resolve, reject) => {
        if (window.XLSX) return resolve(window.XLSX);
        const s = document.createElement('script');
        s.src = 'https://cdn.sheetjs.com/xlsx-0.20.3/package/dist/xlsx.full.min.js';
        s.onload = () => resolve(window.XLSX);
        s.onerror = () => reject(new Error('Could not load the Excel reader — check your connection, or save the file as CSV.'));
        document.head.appendChild(s);
    });

    const onImportFile = async (file) => {
        if (!file) return;
        try {
            let rows;
            if (file.name.toLowerCase().endsWith('.csv')) {
                rows = parseCSV(await file.text());
            } else {
                const XLSX = await loadSheetJS();
                const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                rows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '' });
            }
            const mats = rowsToMaterials(rows);
            if (!mats.length) { showToast && showToast('No rows found. First row must have headers like Category, Material, Size, Unit.'); return; }
            setImportRows(mats);
        } catch (e) { showToast && showToast(e.message || 'Could not read the file.'); }
    };

    const runImport = async () => {
        if (!importRows) return;
        setImporting(true); setImportDone(0);
        let ok = 0, fail = 0;
        const queue = [...importRows];
        const worker = async () => {
            while (queue.length) {
                const m = queue.shift();
                try { await api('materials', 'POST', m); ok++; }
                catch { fail++; }
                setImportDone(d => d + 1);
            }
        };
        await Promise.all([worker(), worker(), worker(), worker()]);
        setImporting(false); setImportRows(null);
        await load();
        showToast && showToast(`Imported ${ok} material${ok !== 1 ? 's' : ''}${fail ? `, ${fail} failed` : ''}.`);
    };

    const downloadTemplate = () => {
        const csv = 'Category,Material,Grade/Spec,Size,Unit,HSN\nCement,OPC 53 Grade,53,,bag,2523\nSteel,TMT Bar,Fe500,16 mm,kg,7214\nAggregate,Coarse Aggregate (Jelly),,20 mm,cft,\n';
        const a = document.createElement('a');
        a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
        a.download = 'materials_import_template.csv'; a.click();
    };

    const inputStyle = {
        width: '100%', padding: '9px 11px', border: '1px solid #e2e8f0',
        borderRadius: '8px', fontSize: '13px', background: '#fff', color: 'var(--slate-900)'
    };
    const th = { textAlign: 'left', padding: '10px 12px', fontSize: '11px', textTransform: 'uppercase',
                 letterSpacing: '0.04em', color: 'var(--slate-500)', borderBottom: '1px solid #eef2f7' };
    const td = { padding: '10px 12px', fontSize: '13px', color: 'var(--slate-800)', borderBottom: '1px solid #f5f7fa' };

    return (
        <div className="card" style={{ padding: '18px' }}>
            {/* toolbar */}
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
                <div style={{ position: 'relative', flex: '1 1 220px' }}>
                    <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--slate-400)' }} />
                    <input placeholder="Search material, grade, size…" value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{ ...inputStyle, paddingLeft: '30px' }} />
                </div>
                <select value={catFilter} onChange={e => setCatFilter(e.target.value)} style={{ ...inputStyle, width: 'auto', minWidth: '150px' }}>
                    <option value="All">All categories</option>
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <label className="btn btn-light" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    <Upload size={14} /> Import
                    <input type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }}
                        onChange={e => { onImportFile(e.target.files?.[0]); e.target.value = ''; }} />
                </label>
                <button className="btn btn-primary" onClick={openAdd}><Plus size={14} /> Add Material</button>
            </div>

            <div style={{ fontSize: '12px', color: 'var(--slate-400)', marginBottom: '8px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                {loading ? 'Loading…' : `${filtered.length} material${filtered.length === 1 ? '' : 's'}${catFilter !== 'All' ? ' in ' + catFilter : ''}`}
                <button onClick={downloadTemplate} style={{ background: 'none', border: 'none', color: '#1743e6', fontSize: '11px', fontWeight: 600, cursor: 'pointer', padding: 0 }}>Download import template</button>
            </div>

            {/* table */}
            <div style={{ overflowX: 'auto', border: '1px solid #eef2f7', borderRadius: '10px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '640px' }}>
                    <thead>
                        <tr>
                            <th style={th}>Category</th>
                            <th style={th}>Material</th>
                            <th style={th}>Grade / Spec</th>
                            <th style={th}>Size</th>
                            <th style={th}>Unit</th>
                            <th style={{ ...th, textAlign: 'right' }}>In Stock</th>
                            <th style={{ ...th, textAlign: 'right' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map(m => (
                            <tr key={m.id}>
                                <td style={td}><span style={{ color: 'var(--slate-500)' }}>{m.category}</span></td>
                                <td style={{ ...td, fontWeight: 600, color: 'var(--slate-900)' }}>{m.name}</td>
                                <td style={td}>{m.spec || '—'}</td>
                                <td style={td}>{m.size
                                    ? <span style={{ fontWeight: 600, color: '#1743e6' }}>{m.size}</span>
                                    : '—'}</td>
                                <td style={td}>{m.unit || '—'}</td>
                                <td style={{ ...td, textAlign: 'right' }}>
                                    <span style={{ fontWeight: 600, color: Number(m.in_stock) > 0 ? '#16a34a' : 'var(--slate-400)' }}>
                                        {Number(m.in_stock || 0)}{m.unit ? ' ' + m.unit : ''}
                                    </span>
                                </td>
                                <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                    <button className="btn btn-light" style={{ fontSize: '11px', padding: '5px 8px', marginRight: '6px' }} onClick={() => openEdit(m)}><Edit2 size={12} /></button>
                                    <button className="btn btn-danger" style={{ fontSize: '11px', padding: '5px 8px' }} onClick={() => remove(m)}><Trash2 size={12} /></button>
                                </td>
                            </tr>
                        ))}
                        {!loading && filtered.length === 0 && (
                            <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: 'var(--slate-400)', padding: '28px' }}>
                                <Layers size={22} style={{ opacity: 0.4, display: 'block', margin: '0 auto 8px' }} />
                                No materials found. Click “Add Material” to create one.
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* add / edit form */}
            {showForm && (
                <div className="modal-overlay" onClick={() => setShowForm(false)}>
                    <div className="modal-box" style={{ maxWidth: '640px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
                        <div className="modal-hdr" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ margin: 0 }}>{editId ? 'Edit Material' : 'Add Material'}</h3>
                            <button className="btn btn-ghost" style={{ padding: '6px' }} onClick={() => setShowForm(false)}><X size={16} /></button>
                        </div>
                        <div style={{ display: 'grid', gap: '12px', padding: '4px 2px 8px', overflowY: 'auto' }}>
                            <div>
                                <label style={{ fontSize: '12px', color: 'var(--slate-500)', display: 'block', marginBottom: '4px' }}>Category *</label>
                                <input list="mat-cats" value={form.category} onChange={e => upd('category', e.target.value)} placeholder="e.g. Steel, Cement, Plumbing" style={inputStyle} />
                                <datalist id="mat-cats">{categories.map(c => <option key={c} value={c} />)}</datalist>
                            </div>
                            <div>
                                <label style={{ fontSize: '12px', color: 'var(--slate-500)', display: 'block', marginBottom: '4px' }}>Material name *</label>
                                <input value={form.name} onChange={e => upd('name', e.target.value)} placeholder="e.g. TMT Steel Bar" style={inputStyle} />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                <div>
                                    <label style={{ fontSize: '12px', color: 'var(--slate-500)', display: 'block', marginBottom: '4px' }}>Grade / Spec</label>
                                    <input value={form.spec} onChange={e => upd('spec', e.target.value)} placeholder="e.g. Fe500D" style={inputStyle} />
                                </div>
                                <div>
                                    <label style={{ fontSize: '12px', color: 'var(--slate-500)', display: 'block', marginBottom: '4px' }}>Size</label>
                                    <input value={form.size} onChange={e => upd('size', e.target.value)} placeholder="e.g. 16 mm" style={inputStyle} />
                                </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                <div>
                                    <label style={{ fontSize: '12px', color: 'var(--slate-500)', display: 'block', marginBottom: '4px' }}>Unit</label>
                                    <input value={form.unit} onChange={e => upd('unit', e.target.value)} placeholder="e.g. kg, bag, nos" style={inputStyle} />
                                </div>
                                <div>
                                    <label style={{ fontSize: '12px', color: 'var(--slate-500)', display: 'block', marginBottom: '4px' }}>HSN (optional)</label>
                                    <input value={form.hsn} onChange={e => upd('hsn', e.target.value)} placeholder="e.g. 7214" style={inputStyle} />
                                </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                                <div>
                                    <label style={{ fontSize: '12px', color: 'var(--slate-500)', display: 'block', marginBottom: '4px' }}>GST %</label>
                                    <input value={form.gst} onChange={e => upd('gst', e.target.value)} placeholder="e.g. 18" style={inputStyle} />
                                </div>
                                <div>
                                    <label style={{ fontSize: '12px', color: 'var(--slate-500)', display: 'block', marginBottom: '4px' }}>Item Code</label>
                                    <input value={form.item_code} onChange={e => upd('item_code', e.target.value)} placeholder="e.g. MRC-001" style={inputStyle} />
                                </div>
                                <div>
                                    <label style={{ fontSize: '12px', color: 'var(--slate-500)', display: 'block', marginBottom: '4px' }}>Cost Code</label>
                                    <input value={form.cost_code} onChange={e => upd('cost_code', e.target.value)} placeholder="optional" style={inputStyle} />
                                </div>
                            </div>
                            <div>
                                <label style={{ fontSize: '12px', color: 'var(--slate-500)', display: 'block', marginBottom: '4px' }}>Opening / In Stock</label>
                                <input type="number" value={form.in_stock} onChange={e => upd('in_stock', e.target.value)} placeholder="0" style={inputStyle} />
                            </div>

                            <div style={{ borderTop: '1px solid #eef2f7', paddingTop: '10px', marginTop: '2px', fontSize: '12px', fontWeight: 700, color: 'var(--slate-600)' }}>Additional specifications</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                <div>
                                    <label style={{ fontSize: '12px', color: 'var(--slate-500)', display: 'block', marginBottom: '4px' }}>Brand</label>
                                    <input value={form.brand} onChange={e => upd('brand', e.target.value)} placeholder="Enter" style={inputStyle} />
                                </div>
                                <div>
                                    <label style={{ fontSize: '12px', color: 'var(--slate-500)', display: 'block', marginBottom: '4px' }}>Variant</label>
                                    <input value={form.variant} onChange={e => upd('variant', e.target.value)} placeholder="Enter" style={inputStyle} />
                                </div>
                                <div>
                                    <label style={{ fontSize: '12px', color: 'var(--slate-500)', display: 'block', marginBottom: '4px' }}>Weight</label>
                                    <input value={form.weight} onChange={e => upd('weight', e.target.value)} placeholder="Enter" style={inputStyle} />
                                </div>
                                <div>
                                    <label style={{ fontSize: '12px', color: 'var(--slate-500)', display: 'block', marginBottom: '4px' }}>Colour</label>
                                    <input value={form.colour} onChange={e => upd('colour', e.target.value)} placeholder="Enter" style={inputStyle} />
                                </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '10px' }}>
                                <div>
                                    <label style={{ fontSize: '12px', color: 'var(--slate-500)', display: 'block', marginBottom: '4px' }}>Length</label>
                                    <input value={form.length} onChange={e => upd('length', e.target.value)} placeholder="Enter" style={inputStyle} />
                                </div>
                                <div>
                                    <label style={{ fontSize: '12px', color: 'var(--slate-500)', display: 'block', marginBottom: '4px' }}>Breadth</label>
                                    <input value={form.breadth} onChange={e => upd('breadth', e.target.value)} placeholder="Enter" style={inputStyle} />
                                </div>
                                <div>
                                    <label style={{ fontSize: '12px', color: 'var(--slate-500)', display: 'block', marginBottom: '4px' }}>Height</label>
                                    <input value={form.height} onChange={e => upd('height', e.target.value)} placeholder="Enter" style={inputStyle} />
                                </div>
                                <div>
                                    <label style={{ fontSize: '12px', color: 'var(--slate-500)', display: 'block', marginBottom: '4px' }}>Diameter</label>
                                    <input value={form.diameter} onChange={e => upd('diameter', e.target.value)} placeholder="Enter" style={inputStyle} />
                                </div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                            <button className="btn btn-light" style={{ flex: 1 }} onClick={() => setShowForm(false)}>Cancel</button>
                            <button className="btn btn-primary" style={{ flex: 1 }} onClick={save} disabled={saving}>
                                <CheckCircle2 size={14} /> {saving ? 'Saving…' : (editId ? 'Save changes' : 'Add material')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Import preview / confirm */}
            {importRows && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, backdropFilter: 'blur(4px)', padding: '20px' }}>
                    <div style={{ background: '#fff', borderRadius: '14px', width: '100%', maxWidth: '620px', maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 48px rgba(0,0,0,0.2)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 22px', borderBottom: '1px solid #eef2f7' }}>
                            <h3 style={{ margin: 0 }}>Import {importRows.length} material{importRows.length !== 1 ? 's' : ''}</h3>
                            {!importing && <button className="btn btn-ghost" style={{ padding: '6px' }} onClick={() => setImportRows(null)}><X size={16} /></button>}
                        </div>
                        <div style={{ padding: '16px 22px', overflowY: 'auto' }}>
                            <div style={{ fontSize: '12px', color: 'var(--slate-500)', marginBottom: '10px' }}>
                                Preview of the first rows. Columns were matched to Category, Material, Grade/Spec, Size, Unit, HSN.
                            </div>
                            <div style={{ border: '1px solid #eef2f7', borderRadius: '8px', overflow: 'hidden' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                    <thead><tr style={{ background: '#f8fafc' }}>
                                        {['Category', 'Material', 'Grade/Spec', 'Size', 'Unit', 'HSN'].map(h => <th key={h} style={{ ...th, padding: '7px 10px' }}>{h}</th>)}
                                    </tr></thead>
                                    <tbody>
                                        {importRows.slice(0, 8).map((m, i) => (
                                            <tr key={i}>
                                                <td style={{ ...td, padding: '7px 10px' }}>{m.category || '—'}</td>
                                                <td style={{ ...td, padding: '7px 10px' }}>{m.name || '—'}</td>
                                                <td style={{ ...td, padding: '7px 10px' }}>{m.spec || '—'}</td>
                                                <td style={{ ...td, padding: '7px 10px' }}>{m.size || '—'}</td>
                                                <td style={{ ...td, padding: '7px 10px' }}>{m.unit || '—'}</td>
                                                <td style={{ ...td, padding: '7px 10px' }}>{m.hsn || '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {importRows.length > 8 && <div style={{ fontSize: '11px', color: 'var(--slate-400)', marginTop: '8px' }}>…and {importRows.length - 8} more.</div>}
                        </div>
                        <div style={{ padding: '14px 22px', borderTop: '1px solid #eef2f7', display: 'flex', gap: '10px', alignItems: 'center', justifyContent: 'flex-end' }}>
                            {importing && <span style={{ fontSize: '12px', color: 'var(--slate-500)', marginRight: 'auto' }}>Importing {importDone}/{importRows.length}…</span>}
                            {!importing && <button className="btn btn-ghost" onClick={() => setImportRows(null)}>Cancel</button>}
                            <button className="btn btn-primary" disabled={importing} onClick={runImport}>
                                {importing ? 'Importing…' : `Import ${importRows.length}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
